import { Component, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '../../../services/language.service';
import { ToastService } from '../../../services/toast.service';
import { AppointmentService } from '../../../services/appointment.service';

export interface AppointmentData {
  name: string;
  age: number | null;
  gender: string;
  whatsappNumber: string;
  address: string;
}

@Component({
  selector: 'app-appointment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './appointment-modal.html',
  styleUrl: './appointment-modal.scss'
})
export class AppointmentModal {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  appointmentData: AppointmentData = {
    name: '',
    age: null,
    gender: '',
    whatsappNumber: '',
    address: ''
  };

  isSubmitting = false;
  validationErrors: { [key: string]: string } = {};

  constructor(
    private languageService: LanguageService,
    private toastService: ToastService,
    private appointmentService: AppointmentService
  ) {}

  // Translation method
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  // Validation methods
  validateName(): void {
    const name = this.appointmentData.name?.trim();
    if (!name) {
      this.validationErrors['name'] = this.t('appointmentNameRequired');
    } else if (name.length < 2) {
      this.validationErrors['name'] = this.t('appointmentNameMinLength');
    } else if (!/^[a-zA-Z\s\u0900-\u097F\u0A00-\u0A7F\u0B00-\u0B7F]+$/.test(name)) {
      this.validationErrors['name'] = this.t('appointmentNameInvalid');
    } else {
      delete this.validationErrors['name'];
    }
  }

  validateAge(): void {
    const age = this.appointmentData.age;
    if (!age) {
      this.validationErrors['age'] = this.t('appointmentAgeRequired');
    } else if (age < 10 || age > 100) {
      this.validationErrors['age'] = this.t('appointmentAgeInvalid');
    } else {
      delete this.validationErrors['age'];
    }
  }

  validateGender(): void {
    if (!this.appointmentData.gender) {
      this.validationErrors['gender'] = this.t('appointmentGenderRequired');
    } else {
      delete this.validationErrors['gender'];
    }
  }

  validateWhatsApp(): void {
    const whatsapp = this.appointmentData.whatsappNumber?.trim();
    if (!whatsapp) {
      this.validationErrors['whatsapp'] = this.t('appointmentWhatsAppRequired');
    } else if (!/^[6-9]\d{9}$/.test(whatsapp)) {
      this.validationErrors['whatsapp'] = this.t('appointmentWhatsAppInvalid');
    } else {
      delete this.validationErrors['whatsapp'];
    }
  }

  validateAddress(): void {
    const address = this.appointmentData.address?.trim();
    if (!address) {
      this.validationErrors['address'] = this.t('appointmentAddressRequired');
    } else if (address.length < 10) {
      this.validationErrors['address'] = this.t('appointmentAddressMinLength');
    } else {
      delete this.validationErrors['address'];
    }
  }

  formatWhatsAppNumber(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    this.appointmentData.whatsappNumber = value;
  }

  formatName(event: any): void {
    let value = event.target.value;
    // Remove numbers and special characters, keep only letters and spaces
    value = value.replace(/[^a-zA-Z\s\u0900-\u097F\u0A00-\u0A7F\u0B00-\u0B7F]/g, '');
    // Remove multiple consecutive spaces
    value = value.replace(/\s+/g, ' ');
    // Trim leading spaces
    value = value.trimStart();
    this.appointmentData.name = value;
  }

  getErrorMessage(field: string): string {
    return this.validationErrors[field] || '';
  }

  isFormValid(): boolean {
    return Object.keys(this.validationErrors).length === 0 &&
           !!(this.appointmentData.name?.trim()) &&
           !!(this.appointmentData.age) &&
           !!(this.appointmentData.gender) &&
           !!(this.appointmentData.whatsappNumber?.trim()) &&
           !!(this.appointmentData.address?.trim());
  }

  onSubmit(): void {
    if (this.isSubmitting) return;

    // Validate all fields
    this.validateName();
    this.validateAge();
    this.validateGender();
    this.validateWhatsApp();
    this.validateAddress();

    // Check if form is valid
    if (!this.isFormValid()) {
      this.toastService.error(this.t('appointmentFormInvalid'));
      return;
    }

    this.isSubmitting = true;

    // Save appointment to Firebase
    this.appointmentService.saveAppointment(this.appointmentData).subscribe({
      next: (response) => {
        if (response.success) {
          console.log('Appointment saved successfully:', response.appointmentId);
          
          // Send appointment details to WhatsApp
          this.sendToWhatsApp();
          
          // Show success message
          this.toastService.success(this.t('appointmentSuccessMessage'));
          
          // Reset form
          this.resetForm();
          
          // Close modal
          this.closeModal.emit();
        } else {
          this.toastService.error(response.message || 'Failed to save appointment');
        }
        this.isSubmitting = false;
      },
      error: (error) => {
        console.error('Error saving appointment:', error);
        // Still send to WhatsApp even if Firebase save fails
        this.sendToWhatsApp();
        this.toastService.error(error.message || 'Failed to save appointment, but WhatsApp message sent');
        this.isSubmitting = false;
      }
    });
  }

  private sendToWhatsApp(): void {
    // Format the appointment details for WhatsApp message
    const message = this.formatAppointmentMessage();
    
    console.log('Final message to send:', message);
    
    // Detect if user is on mobile or desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create WhatsApp URL with the message
    const whatsappUrl = isMobile 
      ? `https://wa.me/918149551184?text=${encodeURIComponent(message)}`
      : `https://web.whatsapp.com/send?phone=918149551184&text=${encodeURIComponent(message)}`;
    
    console.log('WhatsApp URL:', whatsappUrl);
    
    // Open WhatsApp with the pre-filled message
    window.open(whatsappUrl, '_blank');
  }

  private formatAppointmentMessage(): string {
    const data = this.appointmentData;
    
    // Get current language
    const currentLanguage = this.languageService.getCurrentLanguage();
    console.log('Current language:', currentLanguage);
    
    // Define messages for each language directly
    let messageTemplate = '';
    
    switch (currentLanguage) {
      case 'hi':
        messageTemplate = `नमस्ते! मैं अपॉइंटमेंट बुक करना चाहता/चाहती हूं। यहां मेरे विवरण हैं:

👤 नाम: {name}
🎂 उम्र: {age}
⚧ लिंग: {gender}
📱 व्हाट्सऐप: {whatsapp}
📍 पता: {address}

कृपया अपॉइंटमेंट की पुष्टि के लिए मुझसे संपर्क करें। धन्यवाद!`;
        break;
      case 'mr':
        messageTemplate = `नमस्कार! मला अपॉइंटमेंट बुक करायचे आहे. येथे माझे तपशील आहेत:

👤 नाव: {name}
🎂 वय: {age}
⚧ लिंग: {gender}
📱 व्हाट्सऐप: {whatsapp}
📍 पत्ता: {address}

कृपया अपॉइंटमेंटची पुष्टी करण्यासाठी माझ्याशी संपर्क साधा। धन्यवाद!`;
        break;
      default:
        messageTemplate = `Hello! I would like to book an appointment. Here are my details:

👤 Name: {name}
🎂 Age: {age}
⚧ Gender: {gender}
📱 WhatsApp: {whatsapp}
📍 Address: {address}

Please contact me to confirm the appointment. Thank you!`;
        break;
    }
    
    console.log('Message template:', messageTemplate);
    console.log('Appointment data:', data);
    
    // Replace placeholders with actual data
    const formattedMessage = messageTemplate
      .replace('{name}', data.name || '')
      .replace('{age}', data.age?.toString() || '')
      .replace('{gender}', this.getGenderDisplayName(data.gender))
      .replace('{whatsapp}', data.whatsappNumber || '')
      .replace('{address}', data.address || '');
    
    console.log('Formatted message:', formattedMessage);
    return formattedMessage;
  }

  private getGenderDisplayName(gender: string): string {
    switch (gender) {
      case 'male':
        return this.t('appointmentMale');
      case 'female':
        return this.t('appointmentFemale');
      case 'other':
        return this.t('appointmentOther');
      default:
        return gender;
    }
  }

  onClose(): void {
    this.resetForm();
    this.closeModal.emit();
  }

  private resetForm(): void {
    this.appointmentData = {
      name: '',
      age: null,
      gender: '',
      whatsappNumber: '',
      address: ''
    };
    this.isSubmitting = false;
    this.validationErrors = {};
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
